import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaCheckCircle, FaEye, FaDownload, FaFilePdf } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Parse from 'parse';
import Agent from '../../models/Agent';

const AgencySignedContractPanel = ({ agent: agentProp }) => {
  const { t } = useTranslation();
  const [contractUrl, setContractUrl] = useState(null);
  const [signedAt, setSignedAt] = useState(null);
  const [loading, setLoading] = useState(!agentProp);

  useEffect(() => {
    if (agentProp) {
      if (agentProp.hasSignedContract) {
        setContractUrl(agentProp.contractSignedUrl);
        setSignedAt(agentProp.contractSignedAt);
      }
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const user = Parse.User.current();
        if (!user) return;

        const agent = await Agent.getAgentByAuthorId(user.id);
        if (!agent?.hasSignedContract) return;

        setContractUrl(agent.contractSignedUrl);
        setSignedAt(agent.contractSignedAt);
      } catch (error) {
        console.error('Error loading signed contract:', error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [agentProp]);

  if (loading || !contractUrl) {
    return null;
  }

  const signedDate = signedAt ? new Date(signedAt).toLocaleDateString() : '';

  return (
    <Panel>
      <Header>
        <FaFilePdf />
        <div>
          <h3>{t('agencyOptions.contractSignedTitle')}</h3>
          {signedDate && (
            <DateText>
              {t('agencyOptions.contractSignedOn', { date: signedDate })}
            </DateText>
          )}
        </div>
        <SignedBadge>
          <FaCheckCircle />
          {t('agencyOptions.signed')}
        </SignedBadge>
      </Header>
      <Message>{t('agencyOptions.contractSignedMessage')}</Message>
      <Actions>
        <ActionLink href={contractUrl} target="_blank" rel="noopener noreferrer">
          <FaEye />
          {t('agencyOptions.viewContract')}
        </ActionLink>
        <ActionLink href={contractUrl} download target="_blank" rel="noopener noreferrer">
          <FaDownload />
          {t('agencyOptions.downloadContract')}
        </ActionLink>
      </Actions>
    </Panel>
  );
};

const Panel = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  border-left: 4px solid #27ae60;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.75rem;

  svg:first-child {
    color: #e74c3c;
    font-size: 1.75rem;
    margin-top: 0.15rem;
  }

  h3 {
    margin: 0;
    font-size: 1.15rem;
    color: #333;
  }
`;

const DateText = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.9rem;
  color: #666;
`;

const SignedBadge = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: #e8f8ef;
  color: #27ae60;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
`;

const Message = styled.p`
  margin: 0 0 1.25rem;
  color: #555;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const ActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  background: #4a90e2;
  color: white;
  transition: background 0.2s;

  &:hover {
    background: #357abd;
    color: white;
  }
`;

export default AgencySignedContractPanel;
